// Shared API access-token acquisition for every frontend fetch.
//
// Why this exists as its own serialized module (instead of a token-acquisition
// copy in each api/*.ts file):
//
// The backend validates an Azure AD token with audience = our SPA app reg
// (api://<STATETAX_API_CLIENT_ID>/access_as_user). MSAL caches the account +
// tokens, but Azure AD caps SPA refresh tokens at 24 hours and won't renew them
// past that. So roughly a day after each interactive login, acquireTokenSilent
// starts failing and a fresh interactive token is required.
//
// The old per-file acquisition handled that by calling acquireTokenRedirect
// straight from the React Query queryFn. The dashboard fires many queries at
// once, so on expiry N calls failed silently and N redirects fired at the same
// instant. MSAL only allows one interaction at a time: the losers threw
// `interaction_in_progress`, and the racing redirects could leave MSAL's
// interaction.status flag STUCK in storage — after which every call (even the
// login button) kept throwing interaction_in_progress until the user manually
// cleared site data. That manual cookie-clear was the recurring "No
// authorization header" toil.
//
// Fixes, in order of importance:
//   1. Serialize: a single in-flight acquisition shared by all callers, so at
//      most one silent call and one redirect ever run concurrently. This is
//      what removes the race that wedges MSAL.
//   2. Loop-safe interactive fallback: redirect only when MSAL says interaction
//      is actually required, and never twice in a row (a redirect just brought
//      us back — if silent still fails it's not an expiry, so surface it instead
//      of bouncing forever).
//   3. Self-heal a stuck flag: if a redirect is refused with
//      interaction_in_progress even though we serialize, the flag is stale from a
//      previously interrupted redirect — clear MSAL's cache and start a clean
//      login automatically, so nobody has to clear cookies by hand.
//
// 🔴 4 (2026-08-18) — THE CONTRACT ITSELF WAS THE FOURTH BUG, and it outranked the
//      other three in blast radius. Every fix above is about *acquiring* a token
//      well. None of them helped, because this module used to answer "I could not
//      get you one" by returning `null` — and all 11 call sites across
//      stateTaxClientsApi.ts and complianceApi.ts read that null, skipped the
//      Authorization header, and sent the request anyway. A fail-closed backend
//      then answered 401 "No authorization header", which the pages rendered to
//      staff as "Failed to load clients: No authorization header": an
//      authentication failure wearing a data-loading failure's clothes.
//
//      A nullable return invites that at every call site. `requireApiAccessToken`
//      THROWS a typed AuthRequiredError instead, so there is no falsy value left
//      to drop on the floor and the request is never sent uncredentialed. The
//      RENAME is the load-bearing half: it is what stops the old shape being
//      reintroduced by habit, because the old name no longer exists to call.
//      See src/lib/authError.ts for the full incident.

import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import { msalInstance } from '../main';
import { apiScopes } from '../config/authConfig';
import { AuthRequiredError } from '../lib/authError';

// After an interactive redirect we record the time. If a silent acquisition
// still fails within this window, the problem is not an expired token (a fresh
// interactive auth just completed) — redirecting again would loop, so we stop.
const INTERACTIVE_RETRY_WINDOW_MS = 30_000;
const INTERACTIVE_MARK_KEY = 'statetax.auth.lastInteractiveAt';

let inFlight: Promise<string> | null = null;

/**
 * Acquire an access token for the state-tax backend.
 *
 * Resolves with a token, or **rejects** with an {@link AuthRequiredError} naming why there
 * isn't one. It never resolves with an absent value: a caller cannot end up holding "no
 * credentials" in a form it can quietly ignore.
 *
 * Concurrent callers share a single acquisition — including its rejection, so a dashboard
 * firing twelve queries into an expired session produces one silent call, at most one
 * redirect, and twelve identically-typed failures rather than twelve uncredentialed
 * requests.
 */
export function requireApiAccessToken(): Promise<string> {
  if (!inFlight) {
    inFlight = acquireApiToken().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function acquireApiToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  if (!account) throw new AuthRequiredError('no-account');
  if (!msalInstance.getActiveAccount()) msalInstance.setActiveAccount(account);

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    clearInteractiveMark();
    return result.accessToken;
  } catch (silentError) {
    if (needsInteraction(silentError)) {
      return acquireInteractively(account, silentError);
    }
    // Transient (e.g. network) — let React Query retry rather than yanking the
    // user into a redirect over a blip.
    console.error('Silent token acquisition failed (will retry):', silentError);
    throw new AuthRequiredError('acquisition-failed');
  }
}

/**
 * Always throws. An interactive path either navigates the page away or has already proved it
 * cannot help — in neither case is there a token to return, and the return type says so.
 */
async function acquireInteractively(account: AccountInfo, silentError: unknown): Promise<never> {
  if (interactiveAttemptedRecently()) {
    console.error('API token still unavailable after interactive auth:', silentError);
    throw new AuthRequiredError('interaction-did-not-help');
  }
  markInteractiveAttempt();

  try {
    await msalInstance.acquireTokenRedirect({ scopes: apiScopes, account });
  } catch (redirectError) {
    if (isInteractionInProgress(redirectError)) {
      // We serialize acquisition, so there is no concurrent redirect of ours; a
      // stuck interaction.status flag is left over from a previously interrupted
      // redirect — exactly the wedge that used to require a manual site-data
      // clear. Clear MSAL's cache and start a clean login so the user recovers
      // without touching browser settings.
      await msalInstance.clearCache();
      await msalInstance.loginRedirect({ scopes: apiScopes }).catch(() => undefined);
      throw new AuthRequiredError('stale-interaction-cleared');
    }
    console.error('Interactive token acquisition failed:', redirectError);
    throw new AuthRequiredError('acquisition-failed');
  }
  // acquireTokenRedirect resolved: the full-page redirect is under way and this
  // document is being torn down. Abandon the request — its response could only
  // ever be discarded, and issuing it races a doomed 401 against the navigation.
  throw new AuthRequiredError('interaction-required');
}

function needsInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error instanceof BrowserAuthError) {
    // monitor_window_timeout: the hidden silent-renew iframe timed out (often a
    // 3rd-party-cookie / lapsed-session situation) and needs interaction.
    return ['interaction_required', 'monitor_window_timeout'].includes(error.errorCode);
  }
  return false;
}

function isInteractionInProgress(error: unknown): boolean {
  return error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress';
}

function interactiveAttemptedRecently(): boolean {
  const raw = sessionStorage.getItem(INTERACTIVE_MARK_KEY);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < INTERACTIVE_RETRY_WINDOW_MS;
}

function markInteractiveAttempt(): void {
  sessionStorage.setItem(INTERACTIVE_MARK_KEY, String(Date.now()));
}

function clearInteractiveMark(): void {
  sessionStorage.removeItem(INTERACTIVE_MARK_KEY);
}
